import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Alert,
  Space,
  Typography,
  Empty,
  Tag,
  Divider,
  Slider,
} from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  AiClassification,
  AiMedicalConfirmation,
  type CreateVideoAiDto,
  type VideoDermatoscopyAi,
} from '../../../../api/dermatoApi';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const CLASSIFICATION_OPTIONS = Object.values(AiClassification).map((v) => ({
  value: v,
  label:
    v === 'BENIGN'
      ? 'Benign'
      : v === 'RISK_LOW'
        ? 'Risc scăzut'
        : v === 'RISK_MODERATE'
          ? 'Risc moderat'
          : v === 'RISK_HIGH'
            ? 'Risc crescut'
            : 'Neconcludent',
}));

const CONFIRMATION_OPTIONS = Object.values(AiMedicalConfirmation).map((v) => ({
  value: v,
  label:
    v === 'CONFIRMED'
      ? 'Confirmat medical'
      : v === 'NOT_CONFIRMED'
        ? 'Neconfirmat medical'
        : 'Neconcludent',
}));

const Step7VideoAi = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const [formAntd] = Form.useForm<CreateVideoAiDto>();
  const [videoAi, setVideoAi] = useState<VideoDermatoscopyAi | null>(null);
  const [loadingExam, setLoadingExam] = useState(false);
  const [saving, setSaving] = useState(false);

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  useEffect(() => {
    if (!lesion?.id) {
      setVideoAi(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingExam(true);
        const v = await dermatoApi.videoAi.getByLesion(lesion.id!);
        if (cancelled) return;
        setVideoAi(v);
        if (v) {
          formAntd.setFieldsValue({ ...v, examDate: dayjs(v.examDate) } as any);
        } else {
          formAntd.resetFields();
          formAntd.setFieldsValue({
            examDate: dayjs(),
            device: 'FotoFinder ATBM',
            algorithmVersion: 'v1.0',
            aiClassification: AiClassification.RISK_LOW,
            aiProbability: 30,
            medicalConfirmation: AiMedicalConfirmation.INCONCLUSIVE,
            aiObservations: '',
            conclusion: '',
            recommendedAction: '',
          } as any);
        }
      } catch {
        if (!cancelled) setVideoAi(null);
      } finally {
        if (!cancelled) setLoadingExam(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id, formAntd]);

  const persist = async (): Promise<boolean> => {
    if (!lesion?.id) return true;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateVideoAiDto = {
        ...values,
        examDate: (values.examDate as unknown as dayjs.Dayjs).toISOString(),
      };
      const saved = videoAi?.id
        ? await dermatoApi.videoAi.update(videoAi.id, dto)
        : await dermatoApi.videoAi.createForLesion(lesion.id, dto);
      setVideoAi(saved);
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading) {
    return <Card loading title="Videodermatoscopie + AI" />;
  }

  if (lesions.length === 0) {
    return (
      <Card title="Videodermatoscopie + AI">
        <Empty description="Nu există leziuni definite. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <RobotOutlined style={{ color: '#1890ff' }} />
          <span>Videodermatoscopie + AI — per leziune</span>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {loadingExam && <Alert type="info" message="Se încarcă evaluarea AI…" />}

      {!lesion && !loadingExam && (
        <Alert type="warning" showIcon message="Selectează o leziune pentru a continua." />
      )}

      {lesion && !loadingExam && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text strong>{lesion.location}</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          <Form form={formAntd} layout="vertical">
            <Form.Item name="examDate" label="Data examinării" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="device" label="Dispozitiv" rules={[{ required: true }]}>
              <Input placeholder="ex: FotoFinder ATBM" />
            </Form.Item>
            <Form.Item name="algorithmVersion" label="Versiune algoritm AI" rules={[{ required: true }]}>
              <Input placeholder="ex: v1.0" />
            </Form.Item>
            <Form.Item name="aiClassification" label="Clasificare AI" rules={[{ required: true }]}>
              <Select options={CLASSIFICATION_OPTIONS} />
            </Form.Item>
            <Form.Item
              name="aiProbability"
              label="Probabilitate malignitate (%)"
              rules={[{ required: true, type: 'number', min: 0, max: 100 }]}
            >
              <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
            </Form.Item>
            <Form.Item name="aiObservations" label="Observații AI" rules={[{ required: true }]}>
              <Input.TextArea rows={3} placeholder="Pattern-uri detectate, scor de asimetrie…" />
            </Form.Item>
            <Form.Item name="medicalConfirmation" label="Confirmare medicală" rules={[{ required: true }]}>
              <Select options={CONFIRMATION_OPTIONS} />
            </Form.Item>
            <Form.Item name="conclusion" label="Concluzie AI" rules={[{ required: true }]}>
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="recommendedAction" label="Acțiune recomandată" rules={[{ required: true }]}>
              <Input.TextArea rows={2} />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Space>
                <Button type="primary" onClick={() => void persist()} loading={saving}>
                  {videoAi ? 'Actualizează' : 'Salvează evaluarea AI'}
                </Button>
                {videoAi && (
                  <Text type="success">✓ Evaluare existentă — {videoAi.id.slice(0, 8)}</Text>
                )}
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Card>
  );
};

export default Step7VideoAi;
